import { postTypes } from '../constants/postTypes';
import { parseStartDate, parseEndDate } from '../utils/parseDate';
import { shortDate } from '../utils/formatters';
import { BROADCAST } from '../constants/platformIds';
import i18next from 'i18next';

/** Row value may be a CSV string, a single string, or an array — API expects one username per entry. */
function normalizeCollaboratingAccountUsernames(value) {
    if (value == null) return null;
    if (Array.isArray(value)) {
        return value.filter(Boolean).flatMap(item =>
            String(item)
                .split(',')
                .map(s => s.trim())
                .filter(Boolean),
        );
    }
    return String(value)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
}

const mapToFilter = [
    /* groupBy | filter-name | accessor property */
    ['rightsholder', 'rightsholderIds', 'rightsholderId'],
    ['sponsor', 'sponsorIds', 'sponsorId'],
    [
        'accountOwnershipType',
        'accountOwnershipTypeIds',
        'accountOwnershipTypeId',
    ],
    ['collection', 'collectionIds', 'collectionId'],
    ['mediaType', 'mediaTypeIds', 'mediaTypeId'],
    ['country', 'networkCountryIds', 'countryId'],
];

function getRowFilters(row, groupBy, globalFilters = {}) {
    const t = i18next.getFixedT(null, 'common');
    const groupSet = new Set(groupBy);
    const filters = mapToFilter.reduce(
        (filters, [groupName, filterName, accesorName]) => {
            if (groupSet.has(groupName))
                filters[filterName] = [row[accesorName]];
            return filters;
        },
        {},
    );

    if (groupSet.has('socialPlatform')) {
        filters.platformPostTypePairs = [{ platformId: row.socialPlatformId }];
    }

    if (groupSet.has('account')) {
        filters.accountUsernames = [row.accountUsername];
        filters.platformPostTypePairs = [{ platformId: row.socialPlatformId }];
    }

    if (groupSet.has('postType')) {
        const postTypeId = postTypes[row.postType];
        if (groupSet.has('socialPlatform')) {
            filters.platformPostTypePairs = [
                { platformId: row.socialPlatformId, postTypeId },
            ];
        } else {
            const { platformPostTypePairs: platforms } = globalFilters;
            filters.platformPostTypePairs = platforms.list
                ? Object.keys(platforms.list).map(platformId => ({
                      platformId,
                      postTypeId,
                  }))
                : [];
        }
    }

    if (groupSet.has('date')) {
        filters.startDate = parseStartDate(row.date);
        filters.endDate = parseEndDate(row.date);
    }

    if (groupSet.has('rightsholderPlacement')) {
        const { rightsholderPlacementId } = row;
        const [rhId, plcId] = Object.entries(rightsholderPlacementId)[0];
        filters.rightsholderPlacementIds = [`${rhId}-${plcId}`];
    }

    if (groupSet.has('mediaUploadName')) {
        filters.mediaUploadNames = [row.mediaUploadName];
    }

    // The `gameName` groupBy option in data explorerfilters maps to the `mediaUploadNames` prop when fetching broadcasts
    if (groupSet.has('gameName')) {
        filters.mediaUploadNames = [row.gameName];
    }

    if (groupSet.has('socialAccountGroup')) {
        filters.socialAccountGroupId = row.socialAccountGroupId;
    }

    if (groupSet.has('authorshipType')) {
        filters.authorshipTypes = [row.authorshipTypeId];
    }

    if (groupSet.has('collaboratingAccounts')) {
        const collaboratingAccountUsernames =
            normalizeCollaboratingAccountUsernames(row.collaboratingAccounts);
        if (
            collaboratingAccountUsernames &&
            collaboratingAccountUsernames.length
        ) {
            filters.collaboratingAccountUsernames =
                collaboratingAccountUsernames;
        } else {
            // Adds standard/not supported authorship type if no collaborating accounts are found
            // So that it properly displays posts without collaborating accounts
            filters.collaboratingAccountUsernames = null;
            if (!groupSet.has('authorshipType')) {
                filters.authorshipTypes = ['unsupported', 'standard'];
            }
        }
    }

    // Build overridingFiltersElement for display
    const overridingFiltersElement = [];

    // Add Placement if rightsholderPlacement is in groupBy
    if (
        groupSet.has('rightsholderPlacement') &&
        row.rightsholderPlacementName
    ) {
        overridingFiltersElement.push({
            title: t('groupByOptions.rightsholderPlacement'),
            elements: [row.rightsholderPlacementName],
        });
    }

    // Add Date if date is in groupBy
    if (groupSet.has('date') && filters.startDate) {
        const formattedDate = shortDate(filters.startDate);
        overridingFiltersElement.push({
            title: t('groupByOptions.date'),
            elements: [formattedDate],
        });
    }

    // Add Platform if socialPlatform or account is in groupBy
    if (groupSet.has('socialPlatform') || groupSet.has('account')) {
        const platformId = row.socialPlatformId;
        let platformName = null;

        // Get platform name from row first (most reliable)
        if (row.socialPlatformName) {
            platformName = row.socialPlatformName;
        } else if (platformId) {
            // Check if platformId matches Broadcast
            const platformIdNum = parseInt(platformId, 10);
            if (platformIdNum === BROADCAST) {
                platformName = 'Broadcast';
            } else if (globalFilters.platformPostTypePairs?.list) {
                // Try to get from globalFilters
                platformName =
                    globalFilters.platformPostTypePairs.list[platformId]
                        ?.name ||
                    globalFilters.platformPostTypePairs.list[platformIdNum]
                        ?.name;
            }
        }

        if (platformName) {
            overridingFiltersElement.push({
                title: t('groupByOptions.socialPlatform'),
                elements: [platformName],
            });
        }
    }

    // Add Account if account is in groupBy
    if (groupSet.has('account') && row.accountUsername) {
        overridingFiltersElement.push({
            title: t('groupByOptions.account'),
            elements: [row.accountUsername],
        });
    }

    // Add Rightsholder if rightsholder is in groupBy
    if (groupSet.has('rightsholder') && row.rightsholderName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.rightsholder'),
            elements: [row.rightsholderName],
        });
    }

    // Add Sponsor if sponsor is in groupBy
    if (groupSet.has('sponsor') && row.sponsorName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.sponsor'),
            elements: [row.sponsorName],
        });
    }

    // Add Collection if collection is in groupBy
    if (groupSet.has('collection') && row.collectionName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.collection'),
            elements: [row.collectionName],
        });
    }

    // Add Media Type if mediaType is in groupBy
    if (groupSet.has('mediaType') && row.mediaTypeName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.mediaType'),
            elements: [row.mediaTypeName],
        });
    }

    // Add Account Ownership Type if accountOwnershipType is in groupBy
    if (groupSet.has('accountOwnershipType') && row.accountOwnershipTypeName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.accountOwnershipType'),
            elements: [row.accountOwnershipTypeName],
        });
    }

    // Add Country if country is in groupBy
    if (groupSet.has('country') && row.countryName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.country'),
            elements: [row.countryName],
        });
    }

    // Add Game Name if gameName is in groupBy
    if (groupSet.has('gameName') && row.gameName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.gameName'),
            elements: [row.gameName],
        });
    }

    // Add Broadcast Name if mediaUploadName is in groupBy
    if (groupSet.has('mediaUploadName') && row.mediaUploadName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.mediaUploadName'),
            elements: [row.mediaUploadName],
        });
    }

    // Add Social Account Group if socialAccountGroup is in groupBy
    if (groupSet.has('socialAccountGroup') && row.socialAccountGroupName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.socialAccountGroup'),
            elements: [row.socialAccountGroupName],
        });
    }

    // Add Season Type if seasonType is in groupBy
    if (groupSet.has('seasonType') && row.seasonTypeName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.seasonType'),
            elements: [row.seasonTypeName],
        });
    }

    // Add Game Type if gameType is in groupBy
    if (groupSet.has('gameType') && row.gameTypeName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.gameType'),
            elements: [row.gameTypeName],
        });
    }

    // Add Home Team if homeTeam is in groupBy
    if (groupSet.has('homeTeam') && row.homeTeamName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.homeTeam'),
            elements: [row.homeTeamName],
        });
    }

    // Add Away Team if awayTeam is in groupBy
    if (groupSet.has('awayTeam') && row.awayTeamName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.awayTeam'),
            elements: [row.awayTeamName],
        });
    }

    // Add Authorship Type if authorshipType is in groupBy
    if (groupSet.has('authorshipType') && row.authorshipTypeName) {
        overridingFiltersElement.push({
            title: t('groupByOptions.authorshipType'),
            elements: [row.authorshipTypeName],
        });
    }

    // Add Collaborating Account if collaboratingAccount is in groupBy
    if (
        groupSet.has('collaboratingAccounts') &&
        (row.collaboratingAccounts ||
            row.collaboratingAccounts === undefined ||
            row.collaboratingAccounts === null)
    ) {
        const collaboratingElements = normalizeCollaboratingAccountUsernames(
            row.collaboratingAccounts,
        );
        if (collaboratingElements && collaboratingElements.length) {
            overridingFiltersElement.push({
                title: t('groupByOptions.collaboratingAccounts'),
                elements: collaboratingElements,
            });
        } else {
            overridingFiltersElement.push({
                title: t('groupByOptions.collaboratingAccounts'),
                elements: [
                    t('groupByOptions.collaboratingAccountsNone', {
                        ns: 'common',
                    }),
                ],
            });
        }
    }

    return {
        filters,
        overridingFiltersElement,
    };
}

export default getRowFilters;
